import { useRef, useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

export interface FilterField {
  key: string;
  label: string;
  type: 'select' | 'text' | 'number_range';
  options?: { value: string; label: string }[];
}

export interface ActiveFilter {
  key: string;
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'contains';
  value: string;
}

const OPERATOR_LABELS: Record<string, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  gt: 'greater than',
  lt: 'less than',
};

function operatorsForType(type: FilterField['type']): ActiveFilter['operator'][] {
  switch (type) {
    case 'select':
      return ['eq', 'neq'];
    case 'text':
      return ['contains', 'eq', 'neq'];
    case 'number_range':
      return ['eq', 'gt', 'lt', 'neq'];
  }
}

export interface FilterChipProps {
  field: FilterField;
  filter: ActiveFilter;
  onChange: (filter: ActiveFilter) => void;
  onRemove: () => void;
}

export function FilterChip({ field, filter, onChange, onRemove }: FilterChipProps) {
  const [opOpen, setOpOpen] = useState(false);
  const opRef = useRef<HTMLDivElement>(null);

  // Close operator dropdown on outside click
  useEffect(() => {
    if (!opOpen) return;
    const handler = (e: MouseEvent) => {
      if (opRef.current && !opRef.current.contains(e.target as Node)) setOpOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [opOpen]);

  const operators = operatorsForType(field.type);

  return (
    <div className="inline-flex items-center gap-0.5 bg-surface-700 border border-surface-600 rounded-lg text-xs h-8 overflow-visible">
      {/* Field label */}
      <span className="pl-2.5 pr-1 text-surface-400 font-medium whitespace-nowrap select-none">
        {field.label}
      </span>

      {/* Operator dropdown */}
      <div className="relative" ref={opRef}>
        <button
          type="button"
          onClick={() => setOpOpen((v) => !v)}
          className="flex items-center gap-0.5 px-1.5 py-1 text-nexus-400 hover:text-nexus-300 transition-colors cursor-pointer"
        >
          {OPERATOR_LABELS[filter.operator]}
          <ChevronDown className="h-3 w-3" />
        </button>
        {opOpen && (
          <div className="absolute top-full left-0 mt-1 bg-surface-800 border border-surface-600 rounded-lg shadow-xl z-50 min-w-[120px] py-1">
            {operators.map((op) => (
              <button
                key={op}
                type="button"
                onClick={() => {
                  onChange({ ...filter, operator: op });
                  setOpOpen(false);
                }}
                className={clsx(
                  'w-full text-left px-3 py-1.5 text-xs transition-colors cursor-pointer',
                  op === filter.operator
                    ? 'text-nexus-400 bg-nexus-500/10'
                    : 'text-surface-300 hover:bg-surface-700',
                )}
              >
                {OPERATOR_LABELS[op]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Value input */}
      {field.type === 'select' && field.options ? (
        <select
          value={filter.value}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          className="bg-transparent text-surface-200 text-xs py-1 pl-1 pr-5 focus:outline-none cursor-pointer appearance-none border-none"
          style={{ backgroundImage: 'none' }}
        >
          <option value="" className="bg-surface-800">Any</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-surface-800">
              {opt.label}
            </option>
          ))}
        </select>
      ) : field.type === 'number_range' ? (
        <input
          type="number"
          value={filter.value}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          placeholder="0"
          className="bg-transparent text-surface-200 text-xs py-1 px-1 w-16 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      ) : (
        <input
          type="text"
          value={filter.value}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          placeholder="value..."
          className="bg-transparent text-surface-200 text-xs py-1 px-1 w-24 focus:outline-none"
          autoFocus
        />
      )}

      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className="px-1.5 py-1 text-surface-500 hover:text-red-400 transition-colors cursor-pointer"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
